import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Layers, ExternalLink } from "lucide-react";
import { api, API, formatPrice } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Combine multiple ClassWallet invoices for the SAME student into one merged PDF invoice.
 * Only shows CW registrations that are:
 *   - not already consolidated
 *   - not themselves a combined invoice
 *   - not paid
 *   - not archived
 */
export default function ConsolidateInvoicesDialog({ regs, onClose, onDone }) {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Group eligible registrations by student_name (case-insensitive)
  const groups = useMemo(() => {
    const eligible = (regs || []).filter((r) =>
      (r.payment_method || "classwallet") === "classwallet" &&
      !r.consolidated_into_registration_id &&
      !r.is_combined_invoice &&
      !r.archived &&
      (r.classwallet_status || "pending") !== "submitted" &&
      r.parent_payment_status !== "paid"
    );
    const byStudent = new Map();
    for (const r of eligible) {
      const key = (r.student_name || "").trim().toLowerCase();
      if (!byStudent.has(key)) byStudent.set(key, { student_name: r.student_name, parent_email: r.parent_email, regs: [] });
      byStudent.get(key).regs.push(r);
    }
    return Array.from(byStudent.values())
      .filter((g) => g.regs.length >= 2)
      .sort((a, b) => b.regs.length - a.regs.length);
  }, [regs]);

  const toggle = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectStudent = (g) => {
    setSelectedStudent(g);
    setCheckedIds(new Set(g.regs.map((r) => r.id)));
    setResult(null);
  };

  const selectedTotal = useMemo(() => {
    if (!selectedStudent) return { count: 0, subtotal: 0 };
    let subtotal = 0;
    for (const r of selectedStudent.regs) {
      if (!checkedIds.has(r.id)) continue;
      const s = Number(r.subtotal || r.price_student || 0);
      subtotal += s;
    }
    return { count: checkedIds.size, subtotal: Math.round(subtotal * 100) / 100 };
  }, [selectedStudent, checkedIds]);

  const consolidate = async () => {
    if (checkedIds.size < 2) {
      toast.error("Pick at least 2 invoices to combine");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/registrations/consolidate", {
        registration_ids: Array.from(checkedIds),
      });
      setResult(data);
      toast.success(`Combined ${data.line_item_count} lines into invoice #${data.invoice_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Consolidation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto" data-testid="consolidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Layers size={20} className="text-gg-green" /> Combine invoices into one
          </DialogTitle>
          <DialogDescription>
            Pick a student, then choose which of their outstanding ClassWallet invoices to roll into a
            single merged invoice PDF (one submission to ClassWallet instead of many).
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="rounded-lg border border-gg-green/40 bg-gg-green/5 p-4 space-y-2" data-testid="consolidate-result">
            <div className="font-semibold text-gg-green">
              ✓ Combined invoice #{result.invoice_number} created
            </div>
            <div className="text-sm text-[var(--gg-text)]">
              Student: <strong>{result.student_name}</strong>
            </div>
            <div className="text-sm">
              {result.line_item_count} line item{result.line_item_count !== 1 ? "s" : ""} · rolled up from #
              {result.source_invoice_numbers.join(", #")}
            </div>
            <div className="text-sm">
              Subtotal <strong>{formatPrice(result.subtotal)}</strong> · 2.04% CW fee{" "}
              <strong>{formatPrice(result.classwallet_fee_amount)}</strong> · Total{" "}
              <strong className="text-gg-green">{formatPrice(result.total)}</strong>
            </div>
            <a
              href={`${API}${result.invoice_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-primary text-sm mt-2 inline-flex"
              data-testid="consolidate-download-link"
            >
              <ExternalLink size={14} /> Open the combined PDF
            </a>
            <p className="text-xs text-gg-muted mt-2">
              The {result.source_invoice_numbers.length} source invoices are now soft-linked to this new one and won&apos;t appear
              in future consolidation lists.
            </p>
          </div>
        ) : !selectedStudent ? (
          <div className="space-y-2 mt-2">
            {groups.length === 0 ? (
              <p className="text-sm text-gg-muted p-4 text-center">
                No student has 2+ outstanding ClassWallet invoices to combine right now.
              </p>
            ) : (
              groups.map((g) => (
                <button
                  key={g.student_name}
                  type="button"
                  onClick={() => selectStudent(g)}
                  className="w-full text-left rounded-lg border border-gg-border bg-gg-bg p-3 hover:border-gg-green transition-colors"
                  data-testid={`consolidate-student-${g.student_name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-[var(--gg-text)]">{g.student_name}</div>
                      <div className="text-xs text-gg-muted mt-0.5">
                        {g.parent_email} · {g.regs.length} outstanding invoice{g.regs.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-gg-brown font-semibold">
                        {formatPrice(g.regs.reduce((s, r) => s + Number(r.subtotal || r.price_student || 0), 0))}
                      </div>
                      <div className="text-xs text-gg-muted">subtotal</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">{selectedStudent.student_name}</div>
                <div className="text-xs text-gg-muted">{selectedStudent.parent_email}</div>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedStudent(null); setCheckedIds(new Set()); }}
                className="text-xs text-gg-muted hover:text-gg-green underline"
              >
                ← Pick different student
              </button>
            </div>
            <div className="rounded-lg border border-gg-border divide-y divide-gg-border">
              {selectedStudent.regs.map((r) => {
                const sub = Number(r.subtotal || r.price_student || 0);
                return (
                  <label key={r.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gg-bg" data-testid={`consolidate-check-${r.id}`}>
                    <input
                      type="checkbox"
                      checked={checkedIds.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        #{r.invoice_number} · {r.class_title || "Class"}
                      </div>
                      <div className="text-xs text-gg-muted">
                        {r.class_date_iso ? new Date(r.class_date_iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        {r.is_recurring_invoice ? ` · ${r.session_count || 0} sessions` : ""}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gg-brown shrink-0">{formatPrice(sub)}</div>
                  </label>
                );
              })}
            </div>
            <div className="rounded-lg bg-gg-bg border border-gg-border p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span>{selectedTotal.count} selected · subtotal</span>
                <span className="font-semibold">{formatPrice(selectedTotal.subtotal)}</span>
              </div>
              <div className="flex justify-between text-xs text-gg-muted">
                <span>+ 2.04% ClassWallet fee</span>
                <span>{formatPrice(+(selectedTotal.subtotal * 0.0204).toFixed(2))}</span>
              </div>
              <div className="flex justify-between text-base pt-1 border-t border-gg-border">
                <span className="font-heading">Combined total</span>
                <span className="font-heading text-gg-green">
                  {formatPrice(+(selectedTotal.subtotal * 1.0204).toFixed(2))}
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <button type="button" onClick={onDone} className="btn-pill btn-outline" data-testid="consolidate-done-btn">
              Done
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
              {selectedStudent && (
                <button
                  type="button"
                  onClick={consolidate}
                  disabled={submitting || checkedIds.size < 2}
                  className="btn-pill btn-primary disabled:opacity-60"
                  data-testid="consolidate-confirm-btn"
                >
                  {submitting ? "Combining…" : `Combine ${checkedIds.size} invoice${checkedIds.size !== 1 ? "s" : ""}`}
                </button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
